import React, { useState, type FormEvent } from 'react';
import { motion } from "framer-motion";
import { Plus, Search, X, Edit2, Trash2, TrendingUp, TrendingDown, Minus, Pencil } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import AdminGMPChart from "@/components/AdminGMPChart";
import { IPO_Legacy } from "@/types/models";
import { formatSignedInr } from "@/lib/numberFormat";

type IpoFormState = Omit<IPO_Legacy, "gmp" | "yesterdayGmp" | "lotSize"> & {
  gmp: string;
  yesterdayGmp: string;
  lotSize: string;
};

const emptyIpo: Omit<IpoFormState, "id"> = {
  company: "", issueSize: "", priceBand: "", openDate: "", closeDate: "", listingDate: "", allotmentDate: "",
  status: "upcoming", subscriptionStatus: "-", gmp: "", yesterdayGmp: "",
  estimatedListing: "", lotSize: "0", exchange: "NSE, BSE",
};

const toDisplayDateInput = (value?: string | null): string => {
  const date = String(value || "").trim();
  if (!date) return "";

  if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
    return date;
  }

  const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}-${month}-${year}`;
  }

  return date;
};

const toApiDate = (value?: string | null): string => {
  const date = String(value || "").trim();
  if (!date) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const displayMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (displayMatch) {
    const [, day, month, year] = displayMatch;
    return `${year}-${month}-${day}`;
  }

  return date;
};

const toFormState = (ipo: IPO_Legacy): IpoFormState => ({
  ...ipo,
  openDate: toDisplayDateInput(ipo.openDate),
  closeDate: toDisplayDateInput(ipo.closeDate),
  listingDate: toDisplayDateInput(ipo.listingDate),
  allotmentDate: toDisplayDateInput(ipo.allotmentDate),
  gmp: String(ipo.gmp ?? 0),
  yesterdayGmp: String(ipo.yesterdayGmp ?? 0),
  lotSize: String(ipo.lotSize ?? 0),
});

const parseDecimalField = (rawValue: string): number => {
  const normalized = String(rawValue).replace(/,/g, "").trim();
  if (!normalized || normalized === "-" || normalized === "." || normalized === "-.") {
    return 0;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toIpoPayload = (form: IpoFormState): IPO_Legacy => ({
  ...form,
  openDate: toApiDate(form.openDate),
  closeDate: toApiDate(form.closeDate),
  listingDate: toApiDate(form.listingDate),
  allotmentDate: toApiDate(form.allotmentDate),
  gmp: parseDecimalField(form.gmp),
  yesterdayGmp: parseDecimalField(form.yesterdayGmp),
  lotSize: Math.max(0, Math.trunc(parseDecimalField(form.lotSize))),
});

const AdminIPOs = () => {
  const { ipos, addIpo, updateIpo, deleteIpo } = useData();
  const [editing, setEditing] = useState<IpoFormState | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const openNew = () => {
    setEditing({ id: Date.now().toString(), ...emptyIpo });
    setIsNew(true);
  };

  const openEdit = (ipo: IPO_Legacy) => {
    setEditing(toFormState(ipo));
    setIsNew(false);
  };

  const handleSave = async () => {
    if (!editing) return;
    const payload = toIpoPayload(editing);
    
    console.log("Saving IPO:", payload);
    console.log("Is new:", isNew);
    
    try {
      if (isNew) {
        console.log("Creating new IPO...");
        await addIpo(payload);
      } else {
        console.log("Updating existing IPO with ID:", payload.id);
        await updateIpo(payload.id, payload);
      }
      console.log("IPO saved successfully!");
      setEditing(null);
    } catch (error) {
      console.error("Error saving IPO:", error);
      alert("Error saving IPO: " + (error.message || "Unknown error"));
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm("Delete this IPO?")) {
      try {
        await deleteIpo(id);
      } catch (error) {
        console.error("Error deleting IPO:", error);
        alert("Error deleting IPO. Please try again.");
      }
    }
  };

  const update = (field: keyof IpoFormState, value: string) => {
    setEditing((prev) => prev ? { ...prev, [field]: value } : null);
  };
  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const toSortableTime = (value?: string | null) => {
    const normalized = toApiDate(value);
    const timestamp = Date.parse(normalized || "");
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const sortedIpos = [...ipos].sort((a, b) => {
    const byOpenDate = toSortableTime(b.openDate) - toSortableTime(a.openDate);
    if (byOpenDate !== 0) {
      return byOpenDate;
    }

    return String(b.company || "").localeCompare(String(a.company || ""));
  });
  const filteredIpos = !normalizedSearch
    ? sortedIpos
    : sortedIpos.filter((ipo) =>
        [
          ipo.company,
          ipo.status,
          ipo.priceBand,
          ipo.issueSize,
          ipo.subscriptionStatus,
          ipo.exchange,
          ipo.openDate,
          ipo.closeDate,
          ipo.listingDate,
          ipo.allotmentDate,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch)
      );
  const totalPages = Math.max(1, Math.ceil(filteredIpos.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedIpos = filteredIpos.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-foreground">Manage IPOs</h1>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
          <Plus size={16} /> Add IPO
        </button>
      </div>
      <form onSubmit={onSearchSubmit} className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by company, status, date, exchange..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
        >
          <Search size={15} />
          Search
        </button>
      </form>

      <div className="finance-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-semibold">Company</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">GMP</th>
                <th className="text-left px-4 py-3 font-semibold">Price Band</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedIpos.map((ipo) => (
                <React.Fragment key={ipo.id}>
                  <tr className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium">{ipo.company}</td>
                    <td className="px-4 py-3">
                      <span className={
                        ipo.status === "upcoming" ? "badge-info" :
                        ipo.status === "ongoing" ? "badge-success" :
                        ipo.status === "closing_today" ? "badge-warning" :
                        ipo.status === "allotment" ? "badge-secondary" :
                        ipo.status === "allotted" ? "badge-success" :
                        ipo.status === "listing_today" ? "badge-primary" :
                        ipo.status === "listed" ? "badge-success" :
                        ipo.status === "closed" ? "badge-destructive" :
                        "badge-warning"
                      }>
                        {ipo.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className={`px-4 py-3 font-semibold ${ipo.gmp >= 0 ? "text-success" : "text-destructive"}`}>
                      {formatSignedInr(ipo.gmp)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{ipo.priceBand}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(ipo)} className="p-1.5 rounded hover:bg-muted"><Pencil size={15} /></button>
                        <button onClick={() => handleDelete(ipo.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-border last:border-0 bg-muted/20">
                    <td colSpan={5} className="px-4 py-3">
                      <AdminGMPChart ipoId={ipo.id} />
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredIpos.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">No IPO records found.</div>
      )}
      {filteredIpos.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filteredIpos.length)} of {filteredIpos.length}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div className="bg-card rounded-xl border border-border p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold">{isNew ? "Add IPO" : "Edit IPO"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Company Name</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.company} onChange={(e) => update("company", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Price Band</label>
                <div className="relative mt-1">
  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
    ₹
  </span>
  <input
    className="w-full pl-8 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
    value={editing.priceBand}
    onChange={(e) => update("priceBand", e.target.value)}
    placeholder="100-110"
  />
</div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Issue Size</label>
                <div className="relative mt-1">
  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
    ₹
  </span>
  <input
    className="w-full pl-8 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
    value={editing.issueSize}
    onChange={(e) => update("issueSize", e.target.value)}
    placeholder="500 cr"
  />
</div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Open Date</label>
                <input 
                  type="text"
                  inputMode="numeric"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" 
                  value={editing.openDate} 
                  onChange={(e) => update("openDate", e.target.value)} 
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Close Date</label>
                <input 
                  type="text"
                  inputMode="numeric"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" 
                  value={editing.closeDate} 
                  onChange={(e) => update("closeDate", e.target.value)} 
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Listing Date</label>
                <input 
                  type="text"
                  inputMode="numeric"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" 
                  value={editing.listingDate} 
                  onChange={(e) => update("listingDate", e.target.value)} 
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Allotment Date</label>
                <input 
                  type="text"
                  inputMode="numeric"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" 
                  value={editing.allotmentDate || ""} 
                  onChange={(e) => update("allotmentDate", e.target.value)} 
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <select className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.status} onChange={(e) => update("status", e.target.value)}>
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="closing_today">Closing Today</option>
                  <option value="allotment">Allotment</option>
                  <option value="allotted">Allotted</option>
                  <option value="listing_today">Listing Today</option>
                  <option value="listed">Listed</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Today's GMP</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                    ₹
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full pl-8 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={editing.gmp}
                    onChange={(e) => update("gmp", e.target.value)}
                    placeholder="0 or -10"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Yesterday's GMP</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                    ₹
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full pl-8 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={editing.yesterdayGmp}
                    onChange={(e) => update("yesterdayGmp", e.target.value)}
                    placeholder="0 or -10"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Est. Listing Price</label>
                <div className="relative mt-1">
  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
    ₹
  </span>
  <input
    className="w-full pl-8 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
    value={editing.estimatedListing}
    onChange={(e) => update("estimatedListing", e.target.value)}
    placeholder="120"
  />
</div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Lot Size</label>
                <input type="text" inputMode="numeric" className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.lotSize} onChange={(e) => update("lotSize", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Subscription</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.subscriptionStatus} onChange={(e) => update("subscriptionStatus", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Exchange</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.exchange} onChange={(e) => update("exchange", e.target.value)} />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button 
                onClick={handleSave} 
                className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
                type="button"
              >
                Save
              </button>
              <button 
                onClick={() => setEditing(null)} 
                className="flex-1 py-2.5 rounded-lg bg-muted text-foreground font-semibold text-sm hover:bg-muted/80 transition-colors"
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIPOs;
